import { Component, EventEmitter, ElementRef, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Location } from '@angular/common';
import { AuthService } from '../../../../core/services/auth.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../../../core/services/toast.service';
import { Subscription } from 'rxjs';
import { LoginLogsService } from '../dashboard/loginLogs/login-logs.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  copied = false;
  windowOrigin = window.location.origin;
  @Output() toggleClick = new EventEmitter<void>();
  @Output() zoomInClick = new EventEmitter<void>();
  @Output() zoomOutClick = new EventEmitter<void>();
  @Output() refreshClick = new EventEmitter<void>();
  /** Coding mode switch; shown only when the hosting chat has an interview selected. */
  @Input() codingModeAvailable = false;
  @Input() codingMode = false;
  @Output() codingModeToggle = new EventEmitter<void>();
  templateId: string = '';
  menuOpen = false;

  currentRoute: string = '';
  isMobile: boolean = false;

  // Routes where the back button should explicitly be shown (prefix match)
  private allowedBackRoutePrefixes: string[] = [
    '/chat/interview',
    '/templates',
    '/chat'
  ];

  // Routes where back button should never show (landing/auth pages)
  private excludedBackRoutePrefixes: string[] = [
    '/',
    '/auth',
    '/dashboard'
  ];



  constructor(
    private authService: AuthService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private elRef: ElementRef,
    private loginLogsService: LoginLogsService,
    private location: Location
  ) {

  }

  private routeSubscription: Subscription | undefined;


  ngOnInit() {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      const newTemplateId = params['templateId'] || '';
      if (newTemplateId !== this.templateId) {
        this.templateId = newTemplateId;
      }
    });
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }


  onZoomIn() {
    this.zoomInClick.emit();
  }

  onZoomOut() {
    this.zoomOutClick.emit();
  }

  onRefresh() {
    this.refreshClick.emit();
  }

  /**
   * Return true when header should show a back button.
   * Shows for routes in allowedBackRoutePrefixes (prefix match),
   * hides for excluded prefixes; otherwise checks history length.
   */
  shouldShowBackButton(): boolean {
    try {
      const url = (this.router.url || '').split('?')[0] || '';
      if (!url) return false;

      for (const ex of this.excludedBackRoutePrefixes) {
        if (ex === '/' ? url === '/' : url.startsWith(ex)) return false;
      }

      for (const p of this.allowedBackRoutePrefixes) {
        if (url.startsWith(p)) return true;
      }

      return (window && window.history && window.history.length > 1);
    } catch (err) {
      return false;
    }
  }

  onBack(): void {
    try {
      this.location.back();
    } catch (err) {
      if (window && window.history) window.history.back();
    }
  }


  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.menuOpen && !this.elRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
  }
  copyCode(): void {
    const link = `${this.windowOrigin}/chat/interview/${this.templateId}`;
    this.copyTextToClipboard(link, () => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    }, (err) => {
      this.toastService.show('Copy failed: ' + err);
    });
  }

  /**
   * Robust clipboard copy with fallback for iframe and browser limitations
   */
  copyTextToClipboard(text: string, onSuccess?: () => void, onError?: (err: any) => void) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => onSuccess && onSuccess())
        .catch(err => {
          // fallback if clipboard API fails
          this.fallbackCopyTextToClipboard(text, onSuccess, onError);
        });
    } else {
      this.fallbackCopyTextToClipboard(text, onSuccess, onError);
    }
  }

  fallbackCopyTextToClipboard(text: string, onSuccess?: () => void, onError?: (err: any) => void) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed'; // Prevent scrolling to bottom
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (successful) {
        onSuccess && onSuccess();
      } else {
        onError && onError('execCommand failed');
      }
    } catch (err) {
      onError && onError(err);
    }
  }

  shareInterview() {
    this.snackBar.open('Share link copied!', 'Close', { duration: 2000 });
  }

  newInterview() {
    this.router.navigate(['/dashboard']);
  }

  signOut() {
    const loginLogId = sessionStorage.getItem('loginLogId');
    if (loginLogId) {
      this.loginLogsService.logUpdate(loginLogId, () => {
        this.authService.signOut();
      });
    } else {
      this.authService.signOut();
    }
  }

  toggleSidebar() {
    this.toggleClick.emit()
  }



}
